import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";
import ModalSpecial from "../Layout/Modals/ModalSpecial";
import Spinner from "../common/Spinner";
import { Checkbox, TimePicker, TreeSelect } from "antd";
import {
  ButtonSecondary,
  DebounceSelect,
  Error,
  InputSwitchComponent,
  Label,
  SelectCountry,
  SelectInput,
  SelectWeekDays,
  Switch,
  TextInput,
} from "../globals";
import { errorHandler, getDateFormat } from "../../helpers/Utils";
import styled from "styled-components";
import RangePickerComp from "../common/RangePickerComp";
import Axios from "axios";
import { putItemRule } from "../../redux/Actions/retail_product_rules_action";
import moment from "moment";
import AntdConfirm from "../../helpers/AntdConfirm";
import { BlockSquare } from "../common/StyledComponents";
import { FaLock } from "react-icons/fa";

// Category titles are Lithuanian, so strip diacritics on both sides: typing "ausine" must still
// find "Ausinės".
const normalizeSearch = (text) =>
  (text ?? "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// TreeSelect hands the whole node over, so match on its title and let antd keep the matching
// branch expanded.
const filterCategoryNode = (input, node) =>
  normalizeSearch(node?.title).includes(normalizeSearch(input));

function ModalRetailRules({ modalFunc, id }) {
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const creating = id == 0;
  const hasSetData = React.useRef(false);

  const { loading, value, setValue } = useFetch(
    `/api/v1/retail_product_rules/${creating ? "new" : id}`
  );
  const { value: value_imum_shops } = useFetch(
    `/api/v1/retail_product_rules/retail_competitors`
  );
  const all_imum_shops = value_imum_shops?.data || [];
  // departmentOptions: [{ key, title }]
  const [departmentOptions, setDepartmentOptions] = useState([]);
  React.useEffect(() => {
    Axios.get("/api/v1/also_incomes/vv_retail_stores")
      .then((res) => {
        const stores = res.data.stores || {};
        // stores: { key: title, ... }
        const options = Object.entries(stores).map(([key, title]) => ({ key, title }));
        setDepartmentOptions(options);
      })
      .catch(() => { });
  }, []);
  // Categories are a small, fixed list (~100), so load it whole once and filter in the browser.
  // The previous per-keystroke debounced fetch meant the dropdown was empty until you typed and
  // every keystroke raced a request; there is nothing to paginate here.
  const [categoryTree, setCategoryTree] = useState([]);
  React.useEffect(() => {
    Axios.get("/api/v1/imum_categories/tree")
      .then((res) => setCategoryTree(res.data?.data || []))
      .catch(() => {
        // Flat fallback, so the field stays usable if the tree cannot be built.
        Axios.get("/api/v1/imum_categories")
          .then((res) =>
            setCategoryTree(
              (res.data || [])
                .filter((category) => category.name)
                .map((category) => ({
                  key: category.id,
                  value: category.id,
                  title: category.name,
                  selectable: true,
                  children: [],
                }))
            )
          )
          .catch(() => { });
      });
  }, []);
  // const { value: imum_rules } = useFetch(
  //   `/api/v1/${window.identifier}/imum_rules?page=1&per_page=25`
  // );

  React.useEffect(() => {
    const fetchDependentRuleDetails = async () => {
      if (value && value.dependent_rule_ids && !hasSetData.current) {
        const ids = value.dependent_rule_ids
          .map((id) => `q[id_in][]=${id}`)
          .join("&");
        if (ids.length === 0) {
          hasSetData.current = true;
          return;
        }
        const { data } = await Axios.get(
          `/api/v1/retail_product_rules?${ids}`
        );

        const dependentRuleDetails = data?.data?.map((rule) => ({
          value: rule.id,
          label: rule.title,
        }));

        setValue((prev) => ({
          ...prev,
          dependent_rule_ids: dependentRuleDetails,
        }));

        hasSetData.current = true;
      }
    };

    if (!loading && value) {
      fetchDependentRuleDetails();
    }
  }, [loading, value]);

  useEffect(() => {
    if (!loading && value && !value.id) {
      setValue((prev) => ({
        ...prev,
        // bb_shop: "tch_retail",
      }));
    }
  }, [loading]);

  // the % switch is shown as selected by default - store it too, otherwise the backend gets no type
  useEffect(() => {
    // set state directly - onChangeParameters is declared after the loading early return,
    // so it is undefined when this effect runs from a loading render
    if (value?.parameters?.discount_enabled && !value.parameters.discount_type) {
      setValue((prev) => ({
        ...prev,
        parameters: { ...(prev.parameters || {}), discount_type: "%" },
      }));
    }
  }, [value?.parameters?.discount_enabled, value?.parameters?.discount_type]);

  // mirrors RetailProductRule#normalized_price_ranges: [From-To] both inclusive,
  // blank From = previous To + 0.01 (or 0), blank To = no upper limit
  const isBlank = (v) => v === "" || v === undefined || v === null;
  // next range From: 1 cent above the previous To ("" when there is no To yet)
  const nextFrom = (to) => (isBlank(to) ? "" : String((Math.round(Number(to) * 100) + 1) / 100));
  const rangeError = React.useMemo(() => {
    const priceRanges = value?.parameters?.price_ranges || [];
    let previousTo = null;
    let previous = null;
    for (const [index, range] of priceRanges.entries()) {
      if (isBlank(range.from) && isBlank(range.to)) continue;
      const from = isBlank(range.from)
        ? (previousTo === null ? 0 : Number(nextFrom(previousTo)))
        : Number(range.from);
      const to = isBlank(range.to) ? null : Number(range.to);
      if (to !== null && from >= to) {
        return `Range ${index + 1}: 'From' must be lower than 'To'`;
      }
      if (previous && (previous.to === null || from <= previous.to)) {
        return `Range ${index + 1} overlaps the previous one - 'From' must be at least 0.01 above the previous 'To'`;
      }
      if (to !== null) previousTo = to;
      previous = { from, to };
    }
    return null;
  }, [value?.parameters?.price_ranges]);

  const discountRange = value?.parameters?.discount_valid_from && value?.parameters?.discount_valid_to
    ? [getDateFormat(value.parameters.discount_valid_from), getDateFormat(value.parameters.discount_valid_to)]
    : ["", ""];

  const discountRangeError = React.useMemo(() => {
    if (!value?.parameters?.discount_enabled) return null;
    if (!value?.parameters?.discount_amount) return "Discount amount is required";
    if (!value?.parameters?.discount_valid_from || !value?.parameters?.discount_valid_to) {
      return "Discount valid period is required";
    }
    if (moment(value.parameters.discount_valid_to).isSameOrBefore(moment(value.parameters.discount_valid_from))) {
      return "Discount end date must be after the start date";
    }
    return null;
  }, [value?.parameters?.discount_enabled, value?.parameters?.discount_amount, value?.parameters?.discount_valid_from, value?.parameters?.discount_valid_to]);

  if (loading) {
    return (
      <ModalSpecial
        style={{
          width: 500,
        }}
        onClose={() => modalFunc(false)}
      >
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ height: "100%" }}
        >
          <Spinner />
        </div>
      </ModalSpecial>
    );
  }

  const onChange = (e) => {
    const { name, value } = e.target;
    setValue((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onChangeParameters = (e) => {
    const { name, value } = e.target;
    setValue((prev) => ({
      ...prev,
      parameters: prev.parameters
        ? {
          ...prev.parameters,
          [name]: value,
        }
        : {
          [name]: value,
        },
    }));
  };

  const onChangeLetPrice = (val) => {
    let custom_value = value?.parameters?.let_prices || "";
    let val_return = "";
    if (custom_value == "all") {
      val_return = val == "lower" ? "raise" : "lower";
    } else if (val == custom_value) {
      val_return = "";
    } else {
      if (custom_value == "") {
        val_return = val;
      } else if (custom_value !== val) {
        val_return = "all";
      }
    }
    onChangeParameters({
      target: {
        name: "let_prices",
        value: val_return,
      },
    });
  };

  const fetchOptions = async (value, type = "family") => {
    try {
      let api_url = `/api/v1/imum_categories?q=${value}`;
      if (type == "brand")
        api_url = `/api/v1/brands?q[title_i_cont_any]=${value}`;
      if (type == "tag")
        api_url = `/api/v1/retail_product_tags?q[title_i_cont_any]=${value}`;
      if (type == "dependent_rule_ids")
        api_url = `/api/v1/retail_product_rules?q[id_not_eq]=${id}&q[title_i_cont_any]=${value}`;
      if (type == "supplier")
        api_url = `/api/v1/suppliers/all?q[name_i_cont_any]=${value}`;

      const { data } = await Axios.get(api_url);

      const items = (type === "dependent_rule_ids" || type == "supplier") ? data.data : data;

      const mapData = items.map((e) => {
        return {
          ...e,
          value: e.id,
          label:
            type == "tag" ? (
              <div className="d-flex align-items-center cursor-pointer">
                <BlockSquare color={e.color} />
                <div
                  className={`ml-1 text-xs`}
                  style={{
                    color: "#737373",
                  }}
                >
                  {e.title}
                </div>
              </div>
            ) : (
              e.title || e.name
            ),
        };
      });
      return mapData;
    } catch (err) {
      errorHandler(err);
      return [];
    }
  };

  const onSave = async () => {
    try {
      setErrors({});
      if (rangeError || discountRangeError) {
        setErrors({ parameters: rangeError || discountRangeError });
        return;
      }
      const newValue = { ...value };

      if (
        Array.isArray(newValue.dependent_rule_ids) &&
        newValue.dependent_rule_ids.length > 0 &&
        typeof newValue.dependent_rule_ids[0] === "object"
      ) {
        newValue.dependent_rule_ids = newValue.dependent_rule_ids.map(
          (item) => item.value
        );
      }

      const { success, errors } = await dispatch(putItemRule(newValue));
      if (success) {
        modalFunc(false);
      } else {
        setErrors(errors);
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <ModalSpecial
      style={{
        width: 500,
      }}
      text={creating ? "Create rule" : "Edit rule"}
      maskClosable
      onClose={() => {
        AntdConfirm({
          title: "Are you sure want to close modal?",
          content: "",
          onOk: () => {
            return modalFunc(false);
          },
        });
      }}
    >
      <div>
        <TextInput
          text="Rule title"
          name="title"
          required={true}
          value={value.title}
          onChange={onChange}
          warning={errors.title}
        />
        <div className="my-2" />

        <Label>
          <span className="font-bold">Assign by</span>
        </Label>
        <FieldHint>
          Products the rule applies to. A product must match every filled filter (categories,
          brands, tags). Empty filters are not used.
        </FieldHint>

        <Label className="text-sm-label" error={errors.categories}>
          Categories
        </Label>
        {/*
          Deliberately `multiple` and not `treeCheckable`: with checkboxes antd propagates checks
          between parent and child, so checking a group would either drop the parent category
          (SHOW_CHILD) or silently add it once all children are checked (SHOW_ALL). Plain multiple
          select keeps `categories` exactly what was clicked, and `selectable: false` branches
          cannot be picked at all.
        */}
        <TreeSelect
          multiple
          treeNodeFilterProp="title"
          filterTreeNode={filterCategoryNode}
          showSearch={true}
          treeLine={true}
          allowClear={true}
          maxTagCount={6}
          treeData={categoryTree}
          value={value.categories || []}
          placeholder={"Search or browse the category tree"}
          dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
          onChange={(categories) =>
            onChange({ target: { name: "categories", value: categories } })
          }
          style={{ width: "100%" }}
        />
        <DebounceSelect
          mode="multiple"
          labelInValue={false}
          label="Brands"
          showSearch={true}
          value={value.brands}
          placeholder={"Type to search"}
          fetchOptions={(val) => fetchOptions(val, "brand")}
          options={value.current_brands}
          onChange={(value) =>
            onChange({
              target: { name: "brands", value },
            })
          }
          style={{
            width: "100%",
          }}
        />

        <Label>Tags</Label>
        <div
          className="p-2"
          style={{
            backgroundColor: "#F5F5F5",
            border: "1px solid #EFEFEF",
            borderRadius: 1,
          }}
        >
          <div className="text-xs">Include</div>
          <DebounceSelect
            className="select-light-blue"
            mode="multiple"
            labelInValue={false}
            showSearch={true}
            value={value.tags}
            placeholder={"Type to search"}
            fetchOptions={(val) => fetchOptions(val, "tag")}
            options={value.current_tags}
            onChange={(value) =>
              onChange({
                target: { name: "tags", value },
              })
            }
            style={{
              width: "100%",
            }}
          />
          <div className="text-xs">Exclude</div>
          <DebounceSelect
            className="select-light-red"
            mode="multiple"
            labelInValue={false}
            showSearch={true}
            value={value.exclude_tags}
            placeholder={"Type to search"}
            fetchOptions={(val) => fetchOptions(val, "tag")}
            options={value.current_exclude_tags}
            onChange={(value) =>
              onChange({
                target: { name: "exclude_tags", value },
              })
            }
            style={{
              width: "100%",
            }}
          />
        </div>

        <Label required={true} error={errors.let_prices}>
          Let prices
        </Label>
        <FieldHint>
          Which direction of change is allowed compared to the current department sell price.
          "Raise" only - lower calculated prices are skipped, "Lower" only - higher ones are
          skipped. Both - any change is applied.
        </FieldHint>
        {[
          {
            label: "Raise",
            value: "raise",
          },
          {
            label: "Lower",
            value: "lower",
          },
        ].map((d) => {
          const active = [d.value, "all"].includes(
            value?.parameters?.let_prices
          );
          return (
            <ApplyActionItemStyled
              key={d.value}
              className="mb-2 cursor-pointer"
              active={active}
              onClick={() => onChangeLetPrice(d.value)}
            >
              <div className={`pl-3 py-2 text-xs text-font font-semi-bold`}>
                <Checkbox
                  checked={active}
                  onChange={() => onChangeLetPrice(d.value)}
                />
                <span
                  className={`pl-2 text-xs text-font font-semi-bold `}
                  style={{
                    color: active ? "#03b0f7" : "#6C6C6C",
                  }}
                >
                  {d.label}
                </span>
              </div>
            </ApplyActionItemStyled>
          );
        })}
        {false && (
          <>
            <div className="my-2" />
            <SelectCountry
              type={"switch"}
              value={value.countries}
              onChange={onChange}
              error={errors.countries}
            />
          </>
        )}

        <div className="my-2" />
        <div className="d-flex align-items-center justify-content-between">
          <Label required={true} error={errors.departments}>Select departments</Label>
          <SelectAllToggle
            allSelected={
              departmentOptions.length > 0 &&
              (value.departments || []).length === departmentOptions.length
            }
            onToggle={(selectAll) =>
              onChange({
                target: {
                  name: "departments",
                  value: selectAll ? departmentOptions.map((d) => d.key) : [],
                },
              })
            }
          />
        </div>
        <FieldHint>
          Departments that receive the calculated price. Cost price is taken from the department
          supplier; if the product is not in the department, the Rivile supplier cost and sell
          price are used.
        </FieldHint>
        <div className="p-2 mb-2">
          {departmentOptions.map((dept) => {
            const selected = (value.departments || []).includes(dept.key);
            return (
              <ApplyActionItemStyled
                key={dept.key}
                className="mb-2 cursor-pointer"
                active={selected}
                onClick={() => {
                  const current = value.departments || [];
                  const next = selected
                    ? current.filter((d) => d !== dept.key)
                    : [...current, dept.key];
                  onChange({ target: { name: "departments", value: next } });
                }}
              >
                <div className="pl-3 py-2 text-xs text-font font-semi-bold">
                  <Checkbox checked={selected} onChange={() => { }} />
                  <span
                    className="pl-2 text-xs text-font font-semi-bold"
                    style={{ color: selected ? "#03b0f7" : "#6C6C6C" }}
                  >
                    {dept.title}
                  </span>
                </div>
              </ApplyActionItemStyled>
            );
          })}
        </div>

        <div className="my-2" />
        <Switch
          label="Discount"
          checked={!!value.parameters?.discount_enabled}
          onChange={() =>
            onChangeParameters({
              target: {
                name: "discount_enabled",
                value: !value.parameters?.discount_enabled,
              },
            })
          }
          text={value.parameters?.discount_enabled ? "Yes" : "No"}
        />
        <FieldHint>
          Discount rule instead of a regular price rule. Discount is calculated from the current
          department price ("new" price, or the sell price when there is none): % - price minus
          given percent, € - price minus given amount. It is valid for the selected period and is
          skipped when another rule's discount is active or overlaps that period.
        </FieldHint>
        {value.parameters?.discount_enabled && (
          <div
            className="p-2 mb-1"
            style={{
              backgroundColor: "#F5F5F5",
              border: "1px solid #EFEFEF",
              borderRadius: 4,
              position: "relative",
            }}
          >
            <Label>Discount amount</Label>
            <div className="d-flex justify-content-between">
              <div style={{ width: "60%" }}>
                <TextInput
                  suffix={value.parameters?.discount_type}
                  name="discount_amount"
                  type="number"
                  value={value.parameters?.discount_amount}
                  onChange={onChangeParameters}
                />
              </div>
              <div style={{ width: "30%" }}>
                <InputSwitchComponent
                  name="discount_type"
                  options={[
                    { label: "%", value: "%" },
                    { label: "€", value: "€" },
                  ]}
                  value={value.parameters?.discount_type || "%"}
                  onChange={onChangeParameters}
                />
              </div>
            </div>
            <div className="my-2" />
            <RangePickerComp
              style={{ width: "100%" }}
              label="Discount valid period"
              placeholder={["Start date", "End date"]}
              value={discountRange}
              disabledDate={(current) => current && current < moment().startOf("day")}
              onCalendarChange={(_, dates) => {
                onChangeParameters({
                  target: { name: "discount_valid_from", value: dates[0] },
                });
                onChangeParameters({
                  target: { name: "discount_valid_to", value: dates[1] },
                });
              }}
            />
            <Error value={discountRangeError} />
          </div>
        )}

        <div className="my-2" />
        {/* <SelectInput
          mode="multiple"
          label="Excluded competitors"
          options={value_imum_shops?.data?.map((value) => ({
            label: value,
            value,
          }))}
          value={value.shops || undefined}
          dropdownMatchSelectWidth={false}
          onChange={(value) => onChange({ target: { name: "shops", value } })}
          // error={errors.competitor_ids}
        /> */}
        {!value.parameters?.discount_enabled && (
          <>
            <Label>Competitors</Label>
            <FieldHint>
              Competitors used to find the cheapest price (only for products inactive in the
              eshop). Include - use only selected shops, Exclude - use all except selected. Only one
              list can be filled.
            </FieldHint>
        <div
          className="p-2"
          style={{
            backgroundColor: "#F5F5F5",
            border: "1px solid #EFEFEF",
            borderRadius: 1,
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="text-xs">Include</div>
            <SelectAllToggle
              disabled={value?.shops?.length > 0}
              allSelected={
                all_imum_shops.length > 0 &&
                (value.included_shops || []).length === all_imum_shops.length
              }
              onToggle={(selectAll) =>
                onChange({
                  target: {
                    name: "included_shops",
                    value: selectAll ? all_imum_shops : [],
                  },
                })
              }
            />
          </div>
          <SelectInput
            mode="multiple"
            options={value_imum_shops?.data?.map((shop) => ({
              label: shop,
              value: shop,
            }))}
            value={value.included_shops || undefined}
            disabled={value?.shops?.length > 0 ? true : false}
            dropdownMatchSelectWidth={false}
            onChange={(value) =>
              onChange({ target: { name: "included_shops", value } })
            }
          />
          <div className="d-flex align-items-center justify-content-between">
            <div className="text-xs">Exclude</div>
            <SelectAllToggle
              disabled={value?.included_shops?.length > 0}
              allSelected={
                all_imum_shops.length > 0 &&
                (value.shops || []).length === all_imum_shops.length
              }
              onToggle={(selectAll) =>
                onChange({
                  target: { name: "shops", value: selectAll ? all_imum_shops : [] },
                })
              }
            />
          </div>
          <SelectInput
            mode="multiple"
            options={value_imum_shops?.data?.map((shop) => ({
              label: shop,
              value: shop,
            }))}
            value={value.shops || undefined}
            dropdownMatchSelectWidth={false}
            disabled={value?.included_shops?.length > 0 ? true : false}
            onChange={(value) => onChange({ target: { name: "shops", value } })}
          />
        </div>

        <div className="my-2"></div>
        <DebounceSelect
          mode="multiple"
          labelInValue={false}
          label="Run after selected rule"
          showSearch={true}
          value={value.dependent_rule_ids}
          placeholder={"Type to search"}
          fetchOptions={(val) => fetchOptions(val, "dependent_rule_ids")}
          onChange={(value) =>
            onChange({
              target: { name: "dependent_rule_ids", value },
            })
          }
          style={{
            width: "100%",
          }}
        />
        <FieldHint>
          This rule runs only after the selected rules were executed in the last 5 hours.
        </FieldHint>
        <div className="my-2" />
        <Switch
          label="Ignore the prime cost"
          checked={value.parameters?.ignore_the_prime_cost}
          onChange={() =>
            onChangeParameters({
              target: {
                name: "ignore_the_prime_cost",
                value: !value.parameters?.ignore_the_prime_cost,
              },
            })
          }
          text={value.parameters?.ignore_the_prime_cost ? "Yes" : "No"}
        />
        <FieldHint>
          When on, the calculated price is not limited by the cost price and competitors cheaper
          than the cost are not skipped.
        </FieldHint>
        {/* <div className="my-2"></div> */}
        {!value.parameters?.ignore_the_prime_cost && (
          <div
            className="p-2 mb-1"
            style={{
              backgroundColor: "#F5F5F5",
              border: "1px solid #EFEFEF",
              borderRadius: 4,
              position: "relative",
            }}
          >
            <Label>Price cannot be less than the cost price</Label>
            <FieldHint>
              Minimum price: cost price plus given % or €. When both limits are filled, the higher
              one is used. Competitors below this minimum are skipped; if none are left, the product
              goes to the cost review list.
            </FieldHint>
            <div className="d-flex justify-content-between">
              <div style={{ width: "60%" }}>
                <TextInput
                  suffix={value.parameters?.price_cost_type}
                  name="price_cost_value"
                  type="number"
                  value={value.parameters?.price_cost_value}
                  onChange={onChangeParameters}
                />
              </div>
              {/* <Label>Price cannot be less than the cost price</Label>
            <div className="d-flex justify-content-between">
              <div
                style={{
                  width: "30%",
                }}
              >
                <InputSwitchComponent
                  name="price_cost_type"
                  options={[
                    {
                      label: "+",
                      value: "+",
                    },
                    {
                      label: "-",
                      value: "-",
                    },
                  ]}
                  value={value.parameters?.price_cost_type}
                  onChange={onChangeParameters}
                />
              </div>
              <div style={{ width: "60%" }}>
                <TextInput
                  name="price_cost_value"
                  type="number"
                  suffix="%"
                  value={value.parameters?.price_cost_value}
                  onChange={onChangeParameters}
                />
              </div>
            </div> */}

              <div
                style={{
                  width: "30%",
                }}
              >
                <InputSwitchComponent
                  name="price_cost_type"
                  options={[
                    {
                      label: "%",
                      value: "%",
                    },
                    {
                      label: "€",
                      value: "€",
                    },
                  ]}
                  value={value.parameters?.price_cost_type}
                  onChange={onChangeParameters}
                />
              </div>
            </div>
            <Label>and not less than:</Label>
            <div className="d-flex justify-content-between">
              <div style={{ width: "60%" }}>
                <TextInput
                  suffix={value.parameters?.price_cost_type_2}
                  name="price_cost_value_2"
                  type="number"
                  value={value.parameters?.price_cost_value_2}
                  onChange={onChangeParameters}
                />
              </div>
              <div
                style={{
                  width: "30%",
                }}
              >
                <InputSwitchComponent
                  name="price_cost_type_2"
                  options={[
                    {
                      label: "%",
                      value: "%",
                    },
                    {
                      label: "€",
                      value: "€",
                    },
                  ]}
                  value={value.parameters?.price_cost_type_2}
                  onChange={onChangeParameters}
                />
              </div>
            </div>
          </div>
        )}
        <div
          className="p-2 mb-2"
        >
          <Label>Price ranges costs</Label>
          <FieldHint>
            Used only for products active in the eshop - their price is the eshop sell price plus
            the range markup, competitors are not used. A range applies when the eshop sell price is
            from "From" to "To", both included, e.g. 0-50 and 50.01-150: a price of 50 uses the
            first range. Empty "From" starts 0.01 above the previous range's "To", empty "To"
            means no upper limit. Ranges must go from low to high prices without overlapping. If no
            range matches, a fallback of +10% (price under 1000) or +100€ (price 1000 and above) is
            applied.
          </FieldHint>
          {(value.parameters?.price_ranges || []).map((range, index) => (
            <div key={index} className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
              <div style={{ flex: 2 }}>
                <TextInput
                  placeholder={
                    index === 0
                      ? "From 0"
                      : `From ${nextFrom((value.parameters?.price_ranges || [])[index - 1]?.to) || ""}`
                  }
                  name="from"
                  type="number"
                  step="0.01"
                  min="0"
                  value={range.from ?? ""}
                  onChange={(e) => {
                    const newRanges = [...(value.parameters?.price_ranges || [])];
                    newRanges[index] = { ...newRanges[index], from: e.target.value };
                    onChangeParameters({ target: { name: "price_ranges", value: newRanges } });
                  }}
                />
              </div>
              <div style={{ flex: 2 }}>
                <TextInput
                  placeholder="To (empty = no limit)"
                  name="to"
                  type="number"
                  step="0.01"
                  min="0"
                  value={range.to}
                  onChange={(e) => {
                    const newRanges = [...(value.parameters?.price_ranges || [])];
                    newRanges[index] = { ...newRanges[index], to: e.target.value };
                    onChangeParameters({ target: { name: "price_ranges", value: newRanges } });
                  }}
                />
              </div>
              <div style={{ flex: 2 }}>
                <TextInput
                  placeholder="Cost value"
                  name="cost_value"
                  type="number"
                  step="0.01"
                  min="0"
                  suffix={range.cost_type || "%"}
                  value={range.cost_value}
                  onChange={(e) => {
                    const newRanges = [...(value.parameters?.price_ranges || [])];
                    newRanges[index] = { ...newRanges[index], cost_value: e.target.value };
                    onChangeParameters({ target: { name: "price_ranges", value: newRanges } });
                  }}
                />
              </div>
              <InputSwitchComponent
                name="cost_type"
                options={[
                  { label: "%", value: "%" },
                  { label: "€", value: "€" },
                ]}
                value={range.cost_type || "%"}
                onChange={(e) => {
                  const newRanges = [...(value.parameters?.price_ranges || [])];
                  newRanges[index] = { ...newRanges[index], cost_type: e.target.value };
                  onChangeParameters({ target: { name: "price_ranges", value: newRanges } });
                }}
              />
              <button
                type="button"
                onClick={() => {
                  const newRanges = [...(value.parameters?.price_ranges || [])];
                  newRanges.splice(index, 1);
                  onChangeParameters({ target: { name: "price_ranges", value: newRanges } });
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: 18, color: "#999", lineHeight: 1 }}>✕</span>
              </button>
            </div>
          ))}
          <Error value={rangeError} />
          <button
            type="button"
            onClick={() => {
              const currentRanges = value.parameters?.price_ranges || [];
              // new range starts 1 cent above the last one's To
              const lastTo = currentRanges[currentRanges.length - 1]?.to;
              const newRanges = [
                ...currentRanges,
                { from: currentRanges.length ? nextFrom(lastTo) : "0", to: "", cost_value: "", cost_type: "%" },
              ];
              onChangeParameters({ target: { name: "price_ranges", value: newRanges } });
            }}
            style={{
              background: "none",
              border: "1.5px solid #03B0F7",
              borderRadius: 20,
              color: "#03B0F7",
              padding: "5px 16px",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              transition: "background 0.15s, color 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#e6f7fd";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "none";
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.background = "#03B0F7";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.background = "#e6f7fd";
              e.currentTarget.style.color = "#03B0F7";
            }}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Add price range
          </button>
        </div>

        <Label>Price adjustment on cheapest competitor price</Label>
        <FieldHint>
          Used only for products inactive in the eshop - their price is the cheapest competitor
          price plus (or minus, with a negative value) given % or €. Price ranges are not used.
        </FieldHint>
        <div className="d-flex justify-content-between">
          <div style={{ width: "60%" }}>
            <TextInput
              name="cheapest_competitor_value"
              type="number"
              suffix={value.parameters?.cheapest_competitor_type}
              value={value.parameters?.cheapest_competitor_value}
              onChange={onChangeParameters}
            />
          </div>
          <div
            style={{
              width: "30%",
            }}
          >
            <InputSwitchComponent
              name="cheapest_competitor_type"
              options={[
                {
                  label: "%",
                  value: "%",
                },
                {
                  label: "€",
                  value: "€",
                },
              ]}
              value={value.parameters?.cheapest_competitor_type}
              onChange={onChangeParameters}
            />
          </div>
        </div>
          </>
        )}

        <div className="my-2"></div>

        <RangePickerComp
          renderExtraFooter={() => (
            <div className="d-flex align-items-center justify-content-center">
              {!!value.valid_from && (
                <div
                  className="text-primary py-2 cursor-pointer"
                  onClick={() => {
                    setValue((prev) => ({
                      ...prev,
                      valid_to: "",
                    }));
                  }}
                >
                  Do not set end date
                </div>
              )}
            </div>
          )}
          style={{
            width: "100%",
          }}
          label="Period"
          required={true}
          placeholder={["Start date", "End date"]}
          disabledDate={() => { }}
          value={
            value.valid_from
              ? [getDateFormat(value.valid_from), getDateFormat(value.valid_to)]
              : ["", ""]
          }
          onCalendarChange={(_, value) => {
            setValue((prev) => ({
              ...prev,
              valid_from: value[0],
              valid_to: value[1],
            }));
          }}
          warning={errors.valid_from}
        />
        <FieldHint>
          Dates when the rule is executed automatically.
        </FieldHint>
        <div className="my-2" />
        <Switch
          label="Status"
          checked={value.active}
          onChange={() =>
            onChange({ target: { name: "active", value: !value.active } })
          }
        />
        <div className="my-2" />
        <ButtonSecondary
          onClick={() => onSave()}
          disabled={!!rangeError || !!discountRangeError}
        >
          {creating ? "Create" : "Update rule"}
        </ButtonSecondary>
      </div>
    </ModalSpecial>
  );
}

export default ModalRetailRules;

const FieldHint = ({ children }) => (
  <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 8 }}>{children}</div>
);

const SelectAllToggle = ({ allSelected, onToggle, disabled = false }) => (
  <span
    className={`text-xs ${disabled ? "" : "cursor-pointer"}`}
    style={{ color: disabled ? "#C4C4C4" : "#03b0f7" }}
    onClick={() => {
      if (disabled) return;
      onToggle(!allSelected);
    }}
  >
    {allSelected ? "Deselect all" : "Select all"}
  </span>
);

const ApplyActionItemStyled = styled.div`
  background: ${(props) => (props.active ? "#edf9fe" : "#fcfcfc")};
  border: 1px solid ${(props) => (props.active ? "#03B0F7" : "#d9d9d9")};
  border-radius: 4px;
`;
